<template>
	<div class="charts-wrapper">
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('total_views')"
			:caption="getString('views')"
			:value="totalViews"
			:last-period-value="totalViewsPrevious"
			:colors="['#dadada', '#0752A3']"
			:series="viewsSeries"
			:xaxis="xaxisLabels"
			no-currency
			:dark="userStore.darkMode"
			:info-button="getString('info_table_views', [userStore.menuWord])"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('views_per_menu', [userStore.menuWord])"
			:caption="bestMenuName"
			:value="bestMenuValue"
			:last-period-value="bestMenuValuePrevious"
			:colors="menuSeries.map(series => series.color)"
			:series="menuSeries"
			:xaxis="xaxisLabels"
			type="area"
			no-currency
			:dark="userStore.darkMode"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('views_per_browser')"
			:caption="bestBrowserName"
			:value="bestBrowserValue"
			:last-period-value="bestBrowserValuePrevious"
			:colors="browserSeries.map(series => series.color)"
			:series="browserSeries"
			:xaxis="xaxisLabels"
			type="area"
			no-currency
			:dark="userStore.darkMode"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('views_per_room')"
			:caption="bestRoomName"
			:value="bestRoomValue"
			:last-period-value="bestRoomValuePrevious"
			:colors="analyticsStore.bestColors"
			:series="roomSeries"
			:xaxis="xaxisLabels"
			type="area"
			no-currency
			:dark="userStore.darkMode"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('translations')"
			:caption="getString('translations')"
			:value="totalTranslations"
			:last-period-value="totalTranslationsPrevious"
			:colors="['#dadada', '#0752A3']"
			:series="translationsSeries"
			:xaxis="xaxisLabels"
			no-currency
			:dark="userStore.darkMode"
			:info-button="getString('info_table_translations', [userStore.menuWord])"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('translations_per_lang')"
			:caption="bestTranslationLanguageName"
			:value="bestTranslationLanguageValue"
			:last-period-value="bestTranslationLanguageValuePrevious"
			:colors="analyticsStore.bestColors"
			:series="translationLanguageSeries"
			:xaxis="xaxisLabels"
			type="area"
			no-currency
			:dark="userStore.darkMode"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('scroll_bottom')"
			:caption="getString('scrolls_done')"
			:value="totalScrollBottom"
			:last-period-value="totalScrollBottomPrevious"
			:colors="['#dadada', '#0752A3']"
			:series="scrollBottomSeries"
			:xaxis="xaxisLabels"
			no-currency
			:dark="userStore.darkMode"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('view_item_image')"
			:caption="getString('enlargements')"
			:value="totalZoomImage"
			:last-period-value="totalZoomImagePrevious"
			:colors="['#dadada', '#0752A3']"
			:series="zoomImageSeries"
			:xaxis="xaxisLabels"
			no-currency
			:dark="userStore.darkMode"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('clicks_categories')"
			:caption="getString('clicks_categories')"
			:value="totalCategoryClicks"
			:last-period-value="totalCategoryClicksPrevious"
			:colors="['#dadada', '#0752A3']"
			:series="categoryClicksSeries"
			:xaxis="xaxisLabels"
			no-currency
			:dark="userStore.darkMode"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('clicks_products')"
			:caption="getString('clicks_products')"
			:value="totalItemClicks"
			:last-period-value="totalItemClicksPrevious"
			:colors="['#dadada', '#0752A3']"
			:series="itemClicksSeries"
			:xaxis="xaxisLabels"
			no-currency
			:dark="userStore.darkMode"
		/>
	</div>
</template>

<script>
import {useAnalyticsStore, useMenuStore} from "@/console-new/stores";
import {useLanguageStore} from "@/shared/stores";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AppChart from "@/shared/components/AppChart.vue";
import {useUserStore} from "@/user/stores";
import AppTable from "@/shared/components/AppTable.vue";

export default {
	setup() {
		const analyticsStore = useAnalyticsStore();
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const menuStore = useMenuStore();
		return {userStore, menuStore, analyticsStore, getString: languageStore.getString};
	},
	name: "ViewsChartsAggregated",
	components: {AppTable, AppChart},
	mixins: [WindowWidth],
	props: {
		dates: {
			type: Object,
			required: true,
			validator: (value) => {
				return value.hasOwnProperty("start") && value.hasOwnProperty("end") && value.hasOwnProperty("previousStart") && value.hasOwnProperty("previousEnd") && value.hasOwnProperty("timeframeIndex");
			}
		},
	},
	computed: {
		selectorsRowHeight() {
			return this.windowWidth > 820 ? 80 : this.windowWidth > 420 ? 150 : 190;
		},
		aggregatedData() {
			return this.analyticsStore.viewsAggregated;
		},
		xaxisLabels() {
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.adjustedDates);
			return xaxisTimes.map(time => time.label);
		},
		adjustedDates() {
			// Same logic as OrdersChartsAggregated for month and year adjustments
			if (this.dates.timeframeIndex === 2 || this.dates.timeframeIndex === 6) {
				const now = new Date();
				let adjustedStart, adjustedEnd, adjustedPreviousStart, adjustedPreviousEnd;
				
				if (this.dates.timeframeIndex === 2) {
					adjustedStart = this.analyticsStore.firstDayOfCurrentMonth();
					adjustedEnd = now;
					const elapsedTime = adjustedEnd.getTime() - adjustedStart.getTime();
					adjustedPreviousStart = this.analyticsStore.firstDayOfLastMonth();
					adjustedPreviousEnd = new Date(adjustedPreviousStart.getTime() + elapsedTime);
				} else {
					adjustedStart = this.analyticsStore.firstDayOfCurrentYear();
					adjustedEnd = now;
					const elapsedTime = adjustedEnd.getTime() - adjustedStart.getTime();
					adjustedPreviousStart = this.analyticsStore.firstDayOfLastYear();
					adjustedPreviousEnd = new Date(adjustedPreviousStart.getTime() + elapsedTime);
				}
				
				return {
					...this.dates,
					start: adjustedStart,
					end: adjustedEnd,
					previousStart: adjustedPreviousStart,
					previousEnd: adjustedPreviousEnd
				};
			}
			return this.dates;
		},
		// Views metrics - calculated from menu data (includes all visits)
		totalViews() {
			return this.calculateTotal(this.aggregatedData.visits_menu, 'visits_count', this.adjustedDates.start, this.adjustedDates.end);
		},
		totalViewsPrevious() {
			return this.calculateTotal(this.aggregatedData.visits_menu, 'visits_count', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
		},
		viewsSeries() {
			if (!this.aggregatedData.visits_menu || this.aggregatedData.visits_menu.length === 0) return [];
			
			const previousData = this.buildTimeSeries(this.aggregatedData.visits_menu, 'visits_count', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
			const currentData = this.buildTimeSeries(this.aggregatedData.visits_menu, 'visits_count', this.adjustedDates.start, this.adjustedDates.end);
			
			// Check if all values are 0
			const hasData = previousData.some(v => v > 0) || currentData.some(v => v > 0);
			if (!hasData) {
				return [];
			}
			
			return [
				{
					name: this.getString('previous_period'),
					data: previousData
				},
				{
					name: this.getString('current_period'),
					data: currentData
				}
			];
		},
		// Events metrics
		totalTranslations() {
			return this.calculateTotal(this.aggregatedData.events, 'translations_count', this.adjustedDates.start, this.adjustedDates.end);
		},
		totalTranslationsPrevious() {
			return this.calculateTotal(this.aggregatedData.events, 'translations_count', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
		},
		translationsSeries() {
			if (!this.aggregatedData.events || this.aggregatedData.events.length === 0) return [];
			
			const previousData = this.buildTimeSeries(this.aggregatedData.events, 'translations_count', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
			const currentData = this.buildTimeSeries(this.aggregatedData.events, 'translations_count', this.adjustedDates.start, this.adjustedDates.end);
			
			// Check if all values are 0
			const hasData = previousData.some(v => v > 0) || currentData.some(v => v > 0);
			if (!hasData) {
				return [];
			}
			
			return [
				{
					name: this.getString('previous_period'),
					data: previousData
				},
				{
					name: this.getString('current_period'),
					data: currentData
				}
			];
		},
		totalScrollBottom() {
			return this.calculateTotal(this.aggregatedData.events, 'scroll_bottom', this.adjustedDates.start, this.adjustedDates.end);
		},
		totalScrollBottomPrevious() {
			return this.calculateTotal(this.aggregatedData.events, 'scroll_bottom', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
		},
		scrollBottomSeries() {
			if (!this.aggregatedData.events || this.aggregatedData.events.length === 0) return [];
			
			const previousData = this.buildTimeSeries(this.aggregatedData.events, 'scroll_bottom', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
			const currentData = this.buildTimeSeries(this.aggregatedData.events, 'scroll_bottom', this.adjustedDates.start, this.adjustedDates.end);
			
			// Check if all values are 0
			const hasData = previousData.some(v => v > 0) || currentData.some(v => v > 0);
			if (!hasData) {
				return [];
			}
			
			return [
				{
					name: this.getString('previous_period'),
					data: previousData
				},
				{
					name: this.getString('current_period'),
					data: currentData
				}
			];
		},
		totalZoomImage() {
			return this.calculateTotal(this.aggregatedData.events, 'zoom_image', this.adjustedDates.start, this.adjustedDates.end);
		},
		totalZoomImagePrevious() {
			return this.calculateTotal(this.aggregatedData.events, 'zoom_image', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
		},
		zoomImageSeries() {
			if (!this.aggregatedData.events || this.aggregatedData.events.length === 0) return [];
			
			const previousData = this.buildTimeSeries(this.aggregatedData.events, 'zoom_image', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
			const currentData = this.buildTimeSeries(this.aggregatedData.events, 'zoom_image', this.adjustedDates.start, this.adjustedDates.end);
			
			// Check if all values are 0
			const hasData = previousData.some(v => v > 0) || currentData.some(v => v > 0);
			if (!hasData) {
				return [];
			}
			
			return [
				{
					name: this.getString('previous_period'),
					data: previousData
				},
				{
					name: this.getString('current_period'),
					data: currentData
				}
			];
		},
		totalMenuClicks() {
			return this.calculateTotal(this.aggregatedData.events, 'menu_clicks', this.adjustedDates.start, this.adjustedDates.end);
		},
		totalMenuClicksPrevious() {
			return this.calculateTotal(this.aggregatedData.events, 'menu_clicks', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
		},
		menuClicksSeries() {
			if (!this.aggregatedData.events || this.aggregatedData.events.length === 0) return [];
			return [
				{
					name: this.getString('previous_period'),
					data: this.buildTimeSeries(this.aggregatedData.events, 'menu_clicks', this.adjustedDates.previousStart, this.adjustedDates.previousEnd)
				},
				{
					name: this.getString('current_period'),
					data: this.buildTimeSeries(this.aggregatedData.events, 'menu_clicks', this.adjustedDates.start, this.adjustedDates.end)
				}
			];
		},
		totalCategoryClicks() {
			return this.calculateTotal(this.aggregatedData.events, 'category_clicks', this.adjustedDates.start, this.adjustedDates.end);
		},
		totalCategoryClicksPrevious() {
			return this.calculateTotal(this.aggregatedData.events, 'category_clicks', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
		},
		categoryClicksSeries() {
			if (!this.aggregatedData.events || this.aggregatedData.events.length === 0) return [];
			
			const previousData = this.buildTimeSeries(this.aggregatedData.events, 'category_clicks', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
			const currentData = this.buildTimeSeries(this.aggregatedData.events, 'category_clicks', this.adjustedDates.start, this.adjustedDates.end);
			
			// Check if all values are 0
			const hasData = previousData.some(v => v > 0) || currentData.some(v => v > 0);
			if (!hasData) {
				return [];
			}
			
			return [
				{
					name: this.getString('previous_period'),
					data: previousData
				},
				{
					name: this.getString('current_period'),
					data: currentData
				}
			];
		},
		totalItemClicks() {
			return this.calculateTotal(this.aggregatedData.events, 'item_clicks', this.adjustedDates.start, this.adjustedDates.end);
		},
		totalItemClicksPrevious() {
			return this.calculateTotal(this.aggregatedData.events, 'item_clicks', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
		},
		itemClicksSeries() {
			if (!this.aggregatedData.events || this.aggregatedData.events.length === 0) return [];
			
			const previousData = this.buildTimeSeries(this.aggregatedData.events, 'item_clicks', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
			const currentData = this.buildTimeSeries(this.aggregatedData.events, 'item_clicks', this.adjustedDates.start, this.adjustedDates.end);
			
			// Check if all values are 0
			const hasData = previousData.some(v => v > 0) || currentData.some(v => v > 0);
			if (!hasData) {
				return [];
			}
			
			return [
				{
					name: this.getString('previous_period'),
					data: previousData
				},
				{
					name: this.getString('current_period'),
					data: currentData
				}
			];
		},
		// Translation languages
		translationLanguageMap() {
			// Create a map for language names
			const languages = {};
			if (this.aggregatedData.translations) {
				const uniqueLanguages = [...new Set(this.aggregatedData.translations.map(t => t.language))];
				uniqueLanguages.forEach((lang, index) => {
					languages[lang] = { 
						name: lang.toUpperCase(), 
						color: this.analyticsStore.bestColors[index % this.analyticsStore.bestColors.length]
					};
				});
			}
			return languages;
		},
		translationLanguageSeries() {
			if (!this.aggregatedData.translations) return [];
			return this.buildDimensionSeries(this.aggregatedData.translations, 'language', 'translation_count', this.translationLanguageMap);
		},
		bestTranslationLanguageName() {
			const best = this.findBestDimension(this.aggregatedData.translations, 'language', 'translation_count', this.adjustedDates.start, this.adjustedDates.end);
			return best ? best.toUpperCase() : '';
		},
		bestTranslationLanguageValue() {
			const best = this.findBestDimension(this.aggregatedData.translations, 'language', 'translation_count', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.translations, 'language', best, 'translation_count', this.adjustedDates.start, this.adjustedDates.end) : 0;
		},
		bestTranslationLanguageValuePrevious() {
			const best = this.findBestDimension(this.aggregatedData.translations, 'language', 'translation_count', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.translations, 'language', best, 'translation_count', this.adjustedDates.previousStart, this.adjustedDates.previousEnd) : 0;
		},
		// Browser analytics
		browserMap() {
			const browsers = {};
			if (this.aggregatedData.visits_browser) {
				const uniqueBrowsers = [...new Set(this.aggregatedData.visits_browser.map(v => v.browser))];
				uniqueBrowsers.forEach(browser => {
					browsers[browser] = { 
						name: browser, 
						color: this.getBrowserColor(browser)
					};
				});
			}
			return browsers;
		},
		browserSeries() {
			if (!this.aggregatedData.visits_browser) return [];
			return this.buildDimensionSeries(this.aggregatedData.visits_browser, 'browser', 'visits_count', this.browserMap);
		},
		bestBrowserName() {
			const best = this.findBestDimension(this.aggregatedData.visits_browser, 'browser', 'visits_count', this.adjustedDates.start, this.adjustedDates.end);
			return best || '';
		},
		bestBrowserValue() {
			const best = this.findBestDimension(this.aggregatedData.visits_browser, 'browser', 'visits_count', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.visits_browser, 'browser', best, 'visits_count', this.adjustedDates.start, this.adjustedDates.end) : 0;
		},
		bestBrowserValuePrevious() {
			const best = this.findBestDimension(this.aggregatedData.visits_browser, 'browser', 'visits_count', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.visits_browser, 'browser', best, 'visits_count', this.adjustedDates.previousStart, this.adjustedDates.previousEnd) : 0;
		},
		// Menu analytics
		menuMap() {
			const menus = {};
			if (this.aggregatedData.menus) {
				this.aggregatedData.menus.forEach(menu => {
					// Check if color already has # prefix
					const color = menu.color ? (menu.color.startsWith('#') ? menu.color : "#" + menu.color) : "#0752A3";
					// Use generic name if menu name is empty
					const name = menu.name || this.userStore.menuWord + ' ' + menu.menu_id;
					menus[menu.menu_id] = { 
						name: name, 
						color: color
					};
				});
			}
			return menus;
		},
		// Filter menu visits data once - exclude null menu_id
		filteredMenuVisits() {
			return this.aggregatedData.visits_menu ? 
				this.aggregatedData.visits_menu.filter(item => item.menu_id !== null) : 
				[];
		},
		menuSeries() {
			if (!this.filteredMenuVisits || this.filteredMenuVisits.length === 0) return [];
			return this.buildDimensionSeries(this.filteredMenuVisits, 'menu_id', 'visits_count', this.menuMap);
		},
		bestMenuName() {
			const best = this.findBestDimension(this.filteredMenuVisits, 'menu_id', 'visits_count', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.menuMap[best]?.name || '' : '';
		},
		bestMenuValue() {
			const best = this.findBestDimension(this.filteredMenuVisits, 'menu_id', 'visits_count', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.filteredMenuVisits, 'menu_id', best, 'visits_count', this.adjustedDates.start, this.adjustedDates.end) : 0;
		},
		bestMenuValuePrevious() {
			const best = this.findBestDimension(this.filteredMenuVisits, 'menu_id', 'visits_count', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.filteredMenuVisits, 'menu_id', best, 'visits_count', this.adjustedDates.previousStart, this.adjustedDates.previousEnd) : 0;
		},
		// Room analytics
		roomMap() {
			const rooms = {};
			if (this.aggregatedData.rooms) {
				this.aggregatedData.rooms.forEach((room, index) => {
					rooms[room.room_id] = { 
						name: room.name, 
						color: this.analyticsStore.bestColors[index % this.analyticsStore.bestColors.length]
					};
				});
			}
			return rooms;
		},
		roomSeries() {
			if (!this.aggregatedData.visits_room) return [];
			return this.buildDimensionSeries(this.aggregatedData.visits_room, 'room_id', 'visits_count', this.roomMap);
		},
		bestRoomName() {
			const best = this.findBestDimension(this.aggregatedData.visits_room, 'room_id', 'visits_count', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.roomMap[best]?.name || '' : '';
		},
		bestRoomValue() {
			const best = this.findBestDimension(this.aggregatedData.visits_room, 'room_id', 'visits_count', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.visits_room, 'room_id', best, 'visits_count', this.adjustedDates.start, this.adjustedDates.end) : 0;
		},
		bestRoomValuePrevious() {
			const best = this.findBestDimension(this.aggregatedData.visits_room, 'room_id', 'visits_count', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.visits_room, 'room_id', best, 'visits_count', this.adjustedDates.previousStart, this.adjustedDates.previousEnd) : 0;
		},
	},
	methods: {
		getBrowserColor(browser) {
			if(browser.includes('Firefox')) {
				return '#ff9500';
			} else if(browser.includes('Chrome')) {
				return '#ff1b2d';
			} else if(browser.includes('Safari')) {
				return '#0078d7';
			} else if(browser.includes('Edge')) {
				return '#1db035';
			} else if(browser.includes('Opera')) {
				return '#891bff';
			} else if(browser.includes('Internet Explorer')) {
				return '#6b2206';
			} else {
				return '#0752A3';
			}
		},
		calculateTotal(data, field, startDate, endDate) {
			if (!data || data.length === 0) return 0;
			
			const bucketColumn = this.aggregatedData.bucketColumn || 'day_bucket';
			
			const filtered = data.filter(row => {
				const date = new Date(row[bucketColumn]);
				if (bucketColumn === 'hour_bucket') {
					const adjustedEndDate = new Date(endDate);
					adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
					return date >= startDate && date <= adjustedEndDate;
				}
				return date >= startDate && date <= endDate;
			});
			
			return filtered.reduce((sum, row) => sum + Number(row[field] || 0), 0);
		},
		buildTimeSeries(data, field, startDate, endDate) {
			if (!data || data.length === 0) return [];
			
			const bucketColumn = this.aggregatedData.bucketColumn || 'day_bucket';
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.adjustedDates);
			
			const series = new Array(xaxisTimes.length).fill(0);
			
			const isPrevious = startDate < this.adjustedDates.start;
			
			const filtered = data.filter(row => {
				const date = new Date(row[bucketColumn]);
				if (bucketColumn === 'hour_bucket') {
					const adjustedEndDate = new Date(endDate);
					adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
					return date >= startDate && date <= adjustedEndDate;
				}
				return date >= startDate && date <= endDate;
			});
			
			filtered.forEach((row) => {
				const bucketDate = new Date(row[bucketColumn]);
				
				if (xaxisTimes.length > 0 && bucketColumn !== 'hour_bucket') {
					bucketDate.setHours(xaxisTimes[0].rawDate.getHours(), xaxisTimes[0].rawDate.getMinutes(), 0, 0);
				}
				
				const mockOrder = { date: bucketDate };
				const index = this.analyticsStore.getDatesIndex(this.adjustedDates, xaxisTimes, mockOrder, isPrevious);
				
				if (index >= 0 && index < series.length) {
					series[index] += Number(row[field] || 0);
				}
			});
			
			return series;
		},
		buildDimensionSeries(data, dimensionField, valueField, dimensionMap) {
			if (!data || data.length === 0) return [];
			
			const bucketColumn = this.aggregatedData.bucketColumn || 'day_bucket';
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.adjustedDates);
			
			const currentData = data.filter(row => {
				const date = new Date(row[bucketColumn]);
				if (bucketColumn === 'hour_bucket') {
					const adjustedEndDate = new Date(this.adjustedDates.end);
					adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
					return date >= this.adjustedDates.start && date <= adjustedEndDate;
				}
				return date >= this.adjustedDates.start && date <= this.adjustedDates.end;
			});
			
			const dimensions = [...new Set(currentData.map(row => row[dimensionField]))];
			
			const allSeries = dimensions.map(dim => {
				const seriesData = new Array(xaxisTimes.length).fill(0);
				
				const dimData = currentData.filter(row => row[dimensionField] === dim);
				
				dimData.forEach(row => {
					const bucketDate = new Date(row[bucketColumn]);
					if (xaxisTimes.length > 0 && bucketColumn !== 'hour_bucket') {
						bucketDate.setHours(xaxisTimes[0].rawDate.getHours(), xaxisTimes[0].rawDate.getMinutes(), 0, 0);
					}
					
					const mockOrder = { date: bucketDate };
					const index = this.analyticsStore.getDatesIndex(this.adjustedDates, xaxisTimes, mockOrder, false);
					
					if (index >= 0 && index < seriesData.length) {
						seriesData[index] += Number(row[valueField] || 0);
					}
				});
				
				const name = dimensionMap[dim]?.name || dim || this.getString('unknown');
				const color = dimensionMap[dim]?.color || '#999999';
				
				const total = seriesData.reduce((sum, val) => sum + val, 0);
				
				return {
					name: name,
					data: seriesData,
					dimensionValue: dim,
					color: color,
					total: total
				};
			});
			
			const sortedSeries = allSeries
				.sort((a, b) => b.total - a.total)
				.slice(0, 6)
				.map(series => {
					const { total, ...seriesWithoutTotal } = series;
					return seriesWithoutTotal;
				});
			
			// Check if all series have only zero values
			const hasData = sortedSeries.some(s => s.data && s.data.some(v => v > 0));
			if (!hasData) {
				return [];
			}
			
			return sortedSeries;
		},
		findBestDimension(data, dimensionField, valueField, startDate, endDate) {
			if (!data || data.length === 0) return null;
			
			const bucketColumn = this.aggregatedData.bucketColumn || 'day_bucket';
			
			const filtered = data.filter(row => {
				const date = new Date(row[bucketColumn]);
				if (bucketColumn === 'hour_bucket') {
					const adjustedEndDate = new Date(endDate);
					adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
					return date >= startDate && date <= adjustedEndDate;
				}
				return date >= startDate && date <= endDate;
			});
			
			const dimensionTotals = {};
			filtered.forEach(row => {
				const dimension = row[dimensionField];
				if (!dimensionTotals[dimension]) {
					dimensionTotals[dimension] = 0;
				}
				dimensionTotals[dimension] += Number(row[valueField] || 0);
			});
			
			let bestDimension = null;
			let bestTotal = 0;
			
			Object.entries(dimensionTotals).forEach(([dimension, total]) => {
				if (total > bestTotal) {
					bestTotal = total;
					bestDimension = dimension;
				}
			});
			
			return bestDimension;
		},
		calculateDimensionTotal(data, dimensionField, dimensionValue, valueField, startDate, endDate) {
			if (!data || data.length === 0) return 0;
			
			const bucketColumn = this.aggregatedData.bucketColumn || 'day_bucket';
			
			const filtered = data.filter(row => {
				const date = new Date(row[bucketColumn]);
				const matchesDimension = String(row[dimensionField]) === String(dimensionValue);
				
				if (bucketColumn === 'hour_bucket') {
					const adjustedEndDate = new Date(endDate);
					adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
					return matchesDimension && date >= startDate && date <= adjustedEndDate;
				}
				return matchesDimension && date >= startDate && date <= endDate;
			});
			
			return filtered.reduce((sum, row) => sum + Number(row[valueField] || 0), 0);
		},
	},
}
</script>

<style lang="scss" scoped>
.charts-wrapper {
	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;
	width: 100%;
	margin-bottom: calc(-1 * var(--main-padding));

	.chart-container {
		min-width: calc((100% - var(--main-padding)) / 2);
		max-width: calc((100% - var(--main-padding)) / 2);
		margin-bottom: var(--main-padding);

		&:deep(.chart-svg-container) {
			transition: min-height .35s ease-in-out;
			height: 200px;
		}

		&.expanded {
			min-width: 100%;
			max-width: 100%;

			&:deep(.chart-svg-container) {
				height: 350px;
			}
		}


		&:deep(.card-header h3) {
			font-size: 18px;
		}

	}

	@media (max-width: 920px) {
		justify-content: space-between;
		.chart-container {
			max-width: calc((100% - var(--main-padding)) / 2);

			&.expanded {
				max-width: 100%;
			}
		}
	}

	@media (max-width: 670px) {
		justify-content: center;
		.chart-container {
			min-width: unset;
			max-width: 100%;
			width: 100%;

			&.expanded {
				max-width: 100%;
			}
		}
	}
}
</style>